/**
 * Authentication Context
 * Purpose: Manage user authentication state and provide auth methods
 * Author: Traklet App
 * Created: 2025
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi, apiUtils } from '../utils/api';
import TokenStorage from '../utils/tokenStorage';
import type { User } from '../utils/api';

// Types
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (username: string, email: string, password: string, confirmPassword?: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = user !== null;
  
  // Debug logging for state changes
  useEffect(() => {
    console.log('[AuthContext] User state changed:', { user, isAuthenticated, isLoading });
  }, [user, isAuthenticated, isLoading]);

  /**
   * Check authentication status with JWT
   */
  const checkAuth = async (): Promise<void> => {
    try {
      // Only set loading if we don't already have a user
      if (!user) {
        setIsLoading(true);
      }
      console.log('[AuthContext] Checking JWT auth...');
      
      // First check local storage for existing token and user
      const storedUser = TokenStorage.getUser();
      const hasToken = TokenStorage.getToken();
      
      if (!hasToken || TokenStorage.isTokenExpired()) {
        console.log('[AuthContext] No valid token found locally');
        TokenStorage.clearAll();
        setUser(null);
        return;
      }
      
      if (storedUser) {
        console.log('[AuthContext] Found stored user, validating with server:', storedUser);
        setUser(storedUser);
      }
      
      // Validate token with server
      const response = await authApi.checkAuth();
      console.log('[AuthContext] JWT auth check response:', response);
      
      if (response.success && response.data?.authenticated) {
        console.log('[AuthContext] JWT user authenticated:', response.data.user);
        setUser(response.data.user);
        TokenStorage.setUser(response.data.user);
      } else {
        console.log('[AuthContext] JWT validation failed');
        TokenStorage.clearAll();
        setUser(null);
      }
    } catch (error) {
      console.error('[AuthContext] JWT auth check failed:', error);
      TokenStorage.clearAll();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Login user with JWT
   */
  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    try {
      console.log('[AuthContext] Starting JWT login process...');
      setIsLoading(true);
      const response = await authApi.login(email, password);
      console.log('[AuthContext] JWT Login API response:', response);

      if (response.success && response.data) {
        const { token, user: userData } = response.data;
        
        if (token && userData) {
          console.log('[AuthContext] Storing JWT token and user data:', userData);
          
          // Store token and user data
          TokenStorage.setToken(token);
          TokenStorage.setUser(userData);
          setUser(userData);
          
          console.log('[AuthContext] JWT login completed successfully');
          return { success: true, message: response.message || 'Login successful' };
        } else {
          console.error('[AuthContext] Invalid login response - missing token or user data');
          return { success: false, message: 'Invalid login response' };
        }
      }
      
      console.log('[AuthContext] JWT login failed:', response.message);
      return { success: false, message: response.message || 'Login failed' };
    } catch (error) {
      console.error('[AuthContext] JWT login error:', error);
      return { success: false, message: apiUtils.getErrorMessage(error) };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Register new user
   */
  const register = async (username: string, email: string, password: string, confirmPassword?: string): Promise<{ success: boolean; message: string }> => {
    try {
      console.log('[AuthContext] Starting registration process...');
      console.log('[AuthContext] Registration data:', { username, email, hasPassword: !!password });
      setIsLoading(true);
      const response = await authApi.register(username, email, password, confirmPassword);
      console.log('[AuthContext] Registration API response:', response);
      console.log('[AuthContext] Response data structure:', JSON.stringify(response.data, null, 2));
      
      if (response.success && response.data) {
        const { token, user: userData } = response.data;
        
        if (token && userData) {
          console.log('[AuthContext] Registration successful, storing JWT token and user data:', userData);
          
          // Store token and user data directly from registration response
          TokenStorage.setToken(token);
          TokenStorage.setUser(userData);
          setUser(userData);
          
          console.log('[AuthContext] Registration completed successfully, user authenticated');
          console.log('[AuthContext] Token stored:', TokenStorage.getToken() ? 'Yes' : 'No');
          console.log('[AuthContext] User stored:', TokenStorage.getUser());
          
          setIsLoading(false);
          
          // Small delay to ensure state updates are processed
          setTimeout(() => {
            console.log('[AuthContext] Post-registration state check:', { 
              user: userData, 
              isAuthenticated: true, 
              currentPath: location.pathname 
            });
          }, 100);
          
          return { success: true, message: response.message || 'Registration successful' };
        } else {
          console.error('[AuthContext] Invalid registration response - missing token or user data');
          setIsLoading(false);
          return { success: false, message: 'Invalid registration response' };
        }
      } else {
        console.log('[AuthContext] Registration failed:', response.message);
        setIsLoading(false);
        return { success: false, message: response.message || 'Registration failed' };
      }
    } catch (error) {
      console.error('[AuthContext] Registration error:', error);
      setIsLoading(false);
      return { success: false, message: apiUtils.getErrorMessage(error) };
    }
  };

  /**
   * Logout user (clear JWT token)
   */
  const logout = async (): Promise<void> => {
    try {
      console.log('[AuthContext] Logging out user...');
      // Clear token and user data
      await authApi.logout();
      setUser(null);
      console.log('[AuthContext] Logout completed');
    } catch (error) {
      console.error('[AuthContext] JWT logout error:', error);
      // Still clear local state even if API call fails
      TokenStorage.clearAll();
      setUser(null);
    }
  };

  /**
   * Handle navigation after authentication state changes
   */
  useEffect(() => {
    const currentPath = location.pathname;
    const isAuthPage = ['/login', '/register'].includes(currentPath);
    
    console.log('[AuthContext] Auth state changed:', { isAuthenticated, isLoading, currentPath, isAuthPage });
    
    // Don't navigate if still loading
    if (isLoading) return;
    
    // If user is authenticated and on auth page, redirect to dashboard
    if (isAuthenticated && isAuthPage) {
      console.log('[AuthContext] User authenticated on auth page, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
    // If user is not authenticated and on protected page, redirect to login
    else if (!isAuthenticated && !isAuthPage && currentPath !== '/') {
      console.log('[AuthContext] User not authenticated on protected page, redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  /**
   * Initialize auth state on component mount
   */
  useEffect(() => {
    console.log('[AuthContext] Initializing JWT auth check...', window.location.pathname);
    // Only run auth check once on mount
    let mounted = true;
    
    const initAuth = async () => {
      if (mounted) {
        await checkAuth();
      }
    };
    
    initAuth();
    
    // Listen for token expiration events
    const handleTokenExpired = () => {
      console.log('[AuthContext] Token expired event received');
      if (mounted) {
        setUser(null);
      }
    };
    
    window.addEventListener('auth-token-expired', handleTokenExpired);
    
    return () => {
      mounted = false;
      window.removeEventListener('auth-token-expired', handleTokenExpired);
    };
  }, []); // Empty dependency array ensures this only runs once



  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;